import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Card, Form, Input, Select, DatePicker, InputNumber, Spin, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import http from '../../../Http';
import utils from '../../../utils';

const { Option } = Select;

const SFCConfigurationAndSchedules = ({ assetId, onDeviceDeleted }) => {
  const { t } = useTranslation();
  const setTip = useContext(utils.tipContext);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [configEditVisible, setConfigEditVisible] = useState(false);
  const [commandEditVisible, setCommandEditVisible] = useState(false);
  const [editingCommand, setEditingCommand] = useState(null);
  const [configForm] = Form.useForm();
  const [commandForm] = Form.useForm();

  // 获取配置和调度数据
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await http.post("sfc/inverter/configuration-and-schedules/get", { 
        asset_id: assetId 
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setTip({ open: true, severity: "error", msg: 'Failed to fetch data' });
      }
    } catch (error) {
      setTip({ open: true, severity: "error", msg: 'Network error' });
      console.error(error);
    }
    setLoading(false);
  };

  // 提交override或reject请求
  const submitAction = async (actionData) => {
    try {
      const response = await http.postJSON("sfc/inverter/override-or-reject/do", actionData);
      const result = await response.json();
      return response.status === 200;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  // Configuration Override
  const handleConfigOverride = () => {
    if (data?.configuration) {
      configForm.setFieldsValue({
        configurationId: data.configuration.data.configurationId,
        reserveStateOfCharge: data.configuration.data.reserveStateOfCharge,
        inverterPowerLimit: data.configuration.data.inverterPowerLimit,
        maximumExportPowerToGrid: data.configuration.data.maximumExportPowerToGrid,
      });
      setConfigEditVisible(true);
    }
  };

  // Configuration Reject
  const handleConfigReject = async () => {
    if (data?.configuration) {
      setSubmitting(true);
      const actionData = {
        asset_id: assetId,
        action: 'reject',
        Override: {
          Configuration: {
            configurationId: data.configuration.data.configurationId,
          },
        },
      };

      const success = await submitAction(actionData);
      if (success) {
        setTip({ open: true, severity: "success", msg: 'Configuration rejected successfully' });
        setData(prev => ({ ...prev, configuration: null }));
      } else {
        setTip({ open: true, severity: "error", msg: 'Failed to reject configuration' });
      }
      setSubmitting(false);
    }
  };

  // Schedules Reject
  const handleSchedulesReject = async () => {
    if (data?.schedules) {
      setSubmitting(true);
      const actionData = {
        asset_id: assetId,
        action: 'reject',
        Override: {
          schedule_id_for_reject: data.schedules.data.scheduleId,
        },
      };

      const success = await submitAction(actionData);
      if (success) {
        setTip({ open: true, severity: "success", msg: 'Schedules rejected successfully' });
        setData(prev => ({ ...prev, schedules: null }));
      } else {
        setTip({ open: true, severity: "error", msg: 'Failed to reject schedules' });
      }
      setSubmitting(false);
    }
  };

  // Command Override
  const handleCommandOverride = (command) => {
    setEditingCommand(command);
    commandForm.setFieldsValue({
      commandId: command.commandId,
      type: command.type,
      begin: moment(command.begin),
      end: moment(command.end),
      targetStateOfCharge: command.targetStateOfCharge,
      targetBatteryPower: command.targetBatteryPower,
    });
    setCommandEditVisible(true);
  };

  // Submit Configuration Override
  const handleConfigSubmit = async (values) => {
    setSubmitting(true);
    const actionData = {
      asset_id: assetId,
      action: 'override',
      Override: {
        Configuration: {
          configurationId: values.configurationId,
          reserveStateOfCharge: values.reserveStateOfCharge,
          inverterPowerLimit: values.inverterPowerLimit,
          maximumExportPowerToGrid: values.maximumExportPowerToGrid,
        },
      },
    };

    const success = await submitAction(actionData);
    if (success) {
      setTip({ open: true, severity: "success", msg: 'Configuration overridden successfully' });
      setConfigEditVisible(false);
      fetchData(); // 刷新数据
    } else {
      setTip({ open: true, severity: "error", msg: 'Failed to override configuration' });
    }
    setSubmitting(false);
  };

  // Submit Command Override
  const handleCommandSubmit = async (values) => {
    setSubmitting(true);
    const actionData = {
      asset_id: assetId,
      action: 'override',
      Override: {
        Command: {
          commandId: values.commandId,
          type: values.type,
          begin: values.begin.toISOString(),
          end: values.end.toISOString(),
          targetStateOfCharge: values.targetStateOfCharge,
          targetBatteryPower: values.targetBatteryPower,
        },
      },
    };

    const success = await submitAction(actionData);
    if (success) {
      setTip({ open: true, severity: "success", msg: 'Command overridden successfully' });
      setCommandEditVisible(false);
      fetchData(); // 刷新数据
    } else {
      setTip({ open: true, severity: "error", msg: 'Failed to override command' });
    }
    setSubmitting(false);
  };

  const handleShowModal = () => {
    setVisible(true);
    fetchData();
  };

  // 删除设备
  const handleDeleteDevice = async () => {
    setDeleting(true);
    try {
      const response = await http.postJSON("sfc/device/delete", { 
        asset_id: assetId 
      });
      if (response.ok) {
        setTip({ open: true, severity: "success", msg: 'Device deleted successfully' });
        setVisible(false);
        // 通知父组件设备已删除
        if (onDeviceDeleted) {
          onDeviceDeleted();
        }
      } else {
        setTip({ open: true, severity: "error", msg: 'Failed to delete device' });
      }
    } catch (error) {
      setTip({ open: true, severity: "error", msg: 'Network error' });
      console.error(error);
    }
    setDeleting(false);
  };

  return (
    <>
      <Button type="default" size="middle" style={{ backgroundColor: "#1d80b5", color: "white" }} onClick={handleShowModal}>
        {t('SFC Configuration and Schedules')}
      </Button>

      {/* 主弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('SFC Configuration and Schedules')}</span>
            <Popconfirm
              title={t('Are you sure you want to unregister this device?')}
              onConfirm={handleDeleteDevice}
              okText={t('Yes')}
              cancelText={t('No')}
              okButtonProps={{ loading: deleting }}
              placement="bottom"
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                loading={deleting}
                disabled={submitting}
              >
                {t('Unregister Device')}
              </Button>
            </Popconfirm>
          </div>
        }
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={800}
      >
        <Spin spinning={loading} tip={t('Loading...')}>
          {/* Configuration 部分 */}
          <div style={{ marginBottom: 24 }}>
            <h3>{t('Configuration')}</h3>
            {data?.configuration ? (
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <p><strong>{t('Configuration ID')}:</strong> {data.configuration.data.configurationId}</p>
                  <p><strong>{t('Reserve State Of Charge')}:</strong> {data.configuration.data.reserveStateOfCharge}%</p>
                  <p><strong>{t('Inverter Power Limit')}:</strong> {data.configuration.data.inverterPowerLimit}W</p>
                  <p><strong>{t('Maximum Export Power To Grid')}:</strong> {data.configuration.data.maximumExportPowerToGrid}W</p>
                  <p><strong>{t('Mode')}:</strong> {data.configuration.data.mode}</p>
                </div>
                <Button type="primary" onClick={handleConfigOverride} style={{ marginRight: 8 }} disabled={submitting}>
                  {t('Override')}
                </Button>
                <Button onClick={handleConfigReject} loading={submitting}>
                  {t('Reject')}
                </Button>
              </Card>
            ) : (
              <Card style={{ backgroundColor: '#f5f5f5' }}>
                <p style={{ color: '#999' }}>{t('No configuration data available')}</p>
                <Button disabled style={{ marginRight: 8 }}>{t('Override')}</Button>
                <Button disabled>{t('Reject')}</Button>
              </Card>
            )}
          </div>

          {/* Schedules 部分 */}
          <div>
            <h3>{t('Schedules')}</h3>
            {data?.schedules?.data?.commands?.length > 0 ? (
              <>
                <div style={{ marginBottom: 16 }}>
                  <Button type="danger" onClick={handleSchedulesReject} loading={submitting}>
                    {t('Reject All Schedules')}
                  </Button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {data.schedules.data.commands.map((command, index) => (
                    <Card key={index} style={{ width: 300 }}>
                      <p><strong>{t('Command ID')}:</strong> {command.commandId}</p>
                      <p><strong>{t('Type')}:</strong> {command.type}</p>
                      <p><strong>{t('Begin Time')}:</strong> {moment(command.begin).format('YYYY-MM-DD HH:mm:ss')}</p>
                      <p><strong>{t('End Time')}:</strong> {moment(command.end).format('YYYY-MM-DD HH:mm:ss')}</p>
                      <p><strong>{t('Target State Of Charge (%)')}:</strong> {command.targetStateOfCharge ?? '-'}</p>
                      <p><strong>{t('Target Battery Power (W)')}:</strong> {command.targetBatteryPower ?? '-'}</p>
                      <Button type="primary" size="small" onClick={() => handleCommandOverride(command)}>
                        {t('Override')}
                      </Button>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card style={{ backgroundColor: '#f5f5f5' }}>
                <p style={{ color: '#999' }}>{t('No schedules data available')}</p>
              </Card>
            )}
          </div>
        </Spin>
      </Modal>

      {/* Configuration 编辑弹窗 */}
      <Modal
        title={t('Edit Configuration')}
        visible={configEditVisible}
        onCancel={() => setConfigEditVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={configForm} onFinish={handleConfigSubmit} layout="vertical">
          <Form.Item name="configurationId" label={t('Configuration ID')} rules={[{ required: true }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="reserveStateOfCharge" label={t('Reserve State Of Charge (%)')}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="inverterPowerLimit" label={t('Inverter Power Limit')+" (W)"}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="maximumExportPowerToGrid" label={t('Maximum Export Power To Grid')+" (W)"}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }} loading={submitting}>
              {t('DO')}
            </Button>
            <Button onClick={() => setConfigEditVisible(false)} disabled={submitting}>
              {t('Cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Command 编辑弹窗 */}
      <Modal
        title={t('Edit Command')}
        visible={commandEditVisible}
        onCancel={() => setCommandEditVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={commandForm} onFinish={handleCommandSubmit} layout="vertical">
          <Form.Item name="commandId" label={t('Command ID')} rules={[{ required: true }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="type" label={t('Type')} rules={[{ required: true }]}>
            <Select>
              <Option value="CHARGE">{t('CHARGE')}</Option>
              <Option value="DISCHARGE">{t('DISCHARGE')}</Option>
              <Option value="IDLE">{t('IDLE')}</Option>
            </Select>
          </Form.Item>
          <Form.Item name="begin" label={t('Begin Time')} rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end" label={t('End Time')} rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="targetStateOfCharge" label={t('Target State Of Charge (%)')}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="targetBatteryPower" label={t('Target Battery Power (W)')}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }} loading={submitting}>
              {t('DO')}
            </Button>
            <Button onClick={() => setCommandEditVisible(false)} disabled={submitting}>
              {t('Cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SFCConfigurationAndSchedules;